import { XCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { useFormik } from 'formik';
import React, { useRef, useState } from 'react';
import * as yup from 'yup';
import { serviceDispatch } from '../../services/service-result';
import { updatePassword } from '../../services/account';
import { Account } from '../../services/models/account';
import { Input, Modal } from '../ui';

interface UpdatePasswordDialogProps {
  isOpen: boolean;
  account: Account;
  onClose: () => void;
}

export default function UpdatePasswordDialog(props: UpdatePasswordDialogProps) {
  const cancelButtonRef = useRef(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      newPassword: '',
      newPasswordConfirmation: '',
    },
    validationSchema: yup.object().shape({
      currentPassword: props.account.hasPassword
        ? yup.string().min(6).max(32).required('Your current password is required')
        : yup.string(),
      newPassword: yup.string().min(6).max(32).required('Your new nassword is required'),
      newPasswordConfirmation: yup
        .string()
        .min(6)
        .max(32)
        .when('newPassword', {
          is: (val: any) => !!(val && val.length > 0),
          then: yup.string().oneOf([yup.ref('newPassword')], 'Both password need to be the same'),
        })
        .required('Password verification required'),
    }),
    onSubmit: values => {
      setIsUpdating(true);
      changePassword(values.newPassword, values.currentPassword);
    },
  });

  const handleOnCancel = () => {
    setIsUpdating(false);
    setErrorMessage('');
    formik.resetForm();
    props.onClose();
  };

  const getCleanError = (source?: string): string => {
    return source?.replace(
      'GraphQL error: Exception while fetching data (/accountPasswordUpdate) : ',
      ''
    ) as string;
  };

  const changePassword = async (newPassword: string, currentPassword: string) => {
    const response = await serviceDispatch(() => updatePassword(newPassword, currentPassword));

    setIsUpdating(false);

    if (response.ok) {
      setTimeout(() => props.onClose(), 250);
    } else {
      setErrorMessage(getCleanError(response.message));
    }
  };

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} initialFocus={cancelButtonRef}>
      <div className="flex flex-col">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          {props.account.hasPassword ? 'Change' : 'Set'} your password
        </h3>
        <div className="mt-2 max-w-xl text-sm text-gray-500">
          <p>{props.account.hasPassword ? 'Change the' : 'Set a new'} password for your account.</p>
        </div>

        {errorMessage !== '' && (
          <div className="rounded-md bg-red-50 p-4 my-2">
            <div className="flex">
              <div className="shrink-0">
                <XCircleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{errorMessage}</p>
              </div>
              <div className="ml-auto pl-3">
                <div className="-mx-1.5 -my-1.5">
                  <button
                    type="button"
                    className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                    onClick={() => setErrorMessage('')}
                  >
                    <span className="sr-only">Dismiss</span>
                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <form
          onSubmit={formik.handleSubmit}
          className="grid grid-cols-1 gap-y-6 gap-x-4 grid-cols-6 mt-3"
        >
          {props.account.hasPassword && (
            <div className="col-span-6">
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                Your current Password
              </label>
              <div className="mt-1">
                <Input
                  id="currentPassword"
                  name="currentPassword"
                  type="password"
                  autoComplete="current-password"
                  required
                  error={
                    formik.touched.currentPassword && formik.errors.currentPassword
                      ? formik.errors.currentPassword
                      : undefined
                  }
                  value={formik.values.currentPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </div>
          )}
          <div className="col-span-6">
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
              Your new Password
            </label>
            <div className="mt-1">
              <Input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="current-password"
                required
                error={
                  formik.touched.newPassword && formik.errors.newPassword
                    ? formik.errors.newPassword
                    : undefined
                }
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
          </div>

          <div className="col-span-6">
            <label
              htmlFor="newPasswordConfirmation"
              className="block text-sm font-medium text-gray-700"
            >
              Verify your new password
            </label>
            <div className="mt-1">
              <Input
                id="newPasswordConfirmation"
                name="newPasswordConfirmation"
                type="password"
                autoComplete="current-password"
                required
                error={
                  formik.touched.newPasswordConfirmation && formik.errors.newPasswordConfirmation
                    ? formik.errors.newPasswordConfirmation
                    : undefined
                }
                value={formik.values.newPasswordConfirmation}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </div>
          </div>

          <div className="col-span-6">
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                className="mt-3 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-xs font-medium rounded-md text-white bg-secondary hover:bg-secondary-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                disabled={isUpdating || !formik.isValid}
              >
                {isUpdating && (
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                Save
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-xs px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary sm:mt-0 sm:w-auto sm:text-sm"
                onClick={handleOnCancel}
                ref={cancelButtonRef}
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}
